import React, { useMemo, useState } from "react";
import { displayName } from "@/lib/names";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { X, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { createProvisional, ProvisionalTag } from "@/components/PersonPicker";
import { UNION_TYPES, UNION_STATUSES } from "@/lib/relations";

/**
 * Multi-select of spouses for a person.
 * value: array of ids
 * onChange: (newIds) => void
 * allPersons: list of {id, first_name, last_name, gender}
 * excludeId: id to exclude (the person being edited)
 */
export const SpouseSelector = ({ value = [], onChange, allPersons = [], excludeId, onCreated, unionDetails = {}, onUnionChange }) => {
  const { t } = useLang();
  const { API } = useAuth();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const byId = useMemo(() => Object.fromEntries(allPersons.map(p => [p.id, p])), [allPersons]);

  const selected = (value || []).map(id => byId[id]).filter(Boolean);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allPersons
      .filter(p => p.id !== excludeId)
      .filter(p => !value.includes(p.id))
      .filter(p => !q || `${displayName(p)} ${p.nickname || ""}`.toLowerCase().includes(q))
      .slice(0, 20);
  }, [allPersons, excludeId, value, query]);

  const add = (id) => {
    onChange([...(value || []), id]);
    setQuery("");
  };
  const remove = (id) => onChange((value || []).filter(x => x !== id));
  const createNew = async () => {
    try {
      const p = await createProvisional(API, query);
      onCreated?.(p);
      add(p.id);
      toast.success(`Fiche provisoire créée : ${displayName(p)}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="spouse-selector">
      <div className="flex flex-wrap gap-2 mb-2">
        {selected.length === 0 && (
          <span className="text-sm italic text-[#6E5B4F]">{t("noSpouses")}</span>
        )}
        {selected.map(p => (
          <span
            key={p.id}
            className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-[#F0E6D8] text-[#3A2312] text-sm border border-[#E3D5CA]"
            data-testid={`spouse-chip-${p.id}`}
          >
            <span className="font-medium">{displayName(p)}</span>
            {onUnionChange && (
              <>
                <select value={unionDetails[p.id]?.type || "unknown"} onChange={e => onUnionChange(p.id, { type: e.target.value })} className="h-6 rounded bg-transparent text-xs border border-[#E3D5CA]" data-testid={`union-type-${p.id}`}>
                  {Object.entries(UNION_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={unionDetails[p.id]?.status || "unknown"} onChange={e => onUnionChange(p.id, { status: e.target.value })} className="h-6 rounded bg-transparent text-xs border border-[#E3D5CA]" data-testid={`union-status-${p.id}`}>
                  {Object.entries(UNION_STATUSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </>
            )}
            {p.is_provisional && <ProvisionalTag />}
            <button
              type="button"
              onClick={() => remove(p.id)}
              className="w-5 h-5 rounded-full bg-[#993D22] text-[#FAF7F2] hover:bg-[#7F311A] inline-flex items-center justify-center"
              aria-label={t("removeSpouse")}
              data-testid={`remove-spouse-${p.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5B4F]" />
        <Input
          placeholder={t("addSpouse")}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="pl-10 bg-[#FAF7F2]"
          data-testid="spouse-search-input"
        />
        {open && (candidates.length > 0 || query.trim()) && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-[#E3D5CA] bg-[#FAF7F2] shadow-lg">
            {candidates.map(p => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); add(p.id); }}
                className="w-full text-left px-3 py-2 hover:bg-[#F0E6D8] text-sm text-[#261911] flex items-center justify-between"
                data-testid={`spouse-option-${p.id}`}
              >
                <span>{displayName(p)} {p.is_provisional && <ProvisionalTag />}</span>
                <span className="label-uppercase text-[#6E5B4F]">{p.birth_year || ""}</span>
              </button>
            ))}
            {query.trim() && (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); createNew(); }}
                className="w-full text-left px-3 py-2 border-t border-[#E3D5CA] hover:bg-[#F0E6D8] text-sm text-[#993D22] flex items-center gap-2"
                data-testid="spouse-create-provisional">
                <UserPlus className="w-4 h-4" /> Créer « {query.trim()} » comme fiche provisoire
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpouseSelector;
